/**
 * A donde lleva cada aviso.
 *
 * La regla: al destino donde se puede HACER algo con el, no a una pantalla que
 * lo repita. El aviso ademas deja un recado para que la pantalla abra lo que
 * hay que abrir —el visor del corte, el renglon del producto— porque eso vive
 * alla y no en el marco (ver `lib/screen-intent.ts`).
 *
 * Vive aparte de la campanita porque son DOS las puertas a un aviso: tocarlo en
 * la bandeja y tocar su notificacion en el telefono (`lib/push-bridge.ts`). Con
 * la regla escrita dos veces, un dia una de las dos llevaria a otro lado.
 */

import { notificationKindSchema, type NotificationKind, type Permission } from '@abarrotes/shared';
import { ROUTES } from '../routes.js';
import type { ScreenIntent } from './screen-intent.js';

export interface NotificationTarget {
  to: string;
  intent?: ScreenIntent;
}

export function notificationTarget(kind: NotificationKind, productName: string | null): NotificationTarget {
  switch (kind) {
    // Caja, con el visor del reporte ya abierto.
    case 'daily_report':
      return { to: ROUTES.cash, intent: { kind: 'daily-report' } };
    // La bandeja de entradas por firmar vive en Personal.
    case 'intake_submitted':
      return { to: ROUTES.staff };
    // "Lo que mandaste" encabeza el inventario de quien propone.
    case 'intake_resolved':
      return { to: ROUTES.inventory };
    // Al renglon del producto, que es donde esta el boton de resurtir. Si por lo
    // que sea no viniera el nombre, el inventario entero sigue siendo un destino
    // util.
    case 'low_stock':
    case 'out_of_stock':
      return {
        to: ROUTES.inventory,
        ...(productName ? { intent: { kind: 'find-product', name: productName } as const } : {}),
      };
  }
}

/**
 * Lo mismo, para lo que llega de afuera —la notificacion del telefono—, donde
 * el tipo viene como texto. La prueba de Configuracion lleva a Configuracion.
 *
 * Un tipo que esta version no conoce devuelve `null` y NO se adivina un
 * destino (CLAUDE.md §13: ningun `switch` sobre datos del servidor tiene rama
 * de rebote). Quien llama abre la app donde estaba; el aviso esta en la
 * campanita, que es donde se lee lo que no se sabe a donde llevar.
 */
export function pushTarget(kind: string, productName: string | null): NotificationTarget | null {
  if (kind === 'push_test') return { to: ROUTES.settings };
  const parsed = notificationKindSchema.safeParse(kind);
  return parsed.success ? notificationTarget(parsed.data, productName) : null;
}

/**
 * Quien ve cada aviso. ES UN ESPEJO: el permiso viaja en cada fila de
 * `notifications` y ahi lo aplican la RLS de la campanita y el envio al
 * telefono (CLAUDE.md §10). Aqui sirve para una sola cosa: no pedirle permiso
 * de avisos a quien nunca va a recibir ninguno —el empleado, que solo cobra—.
 * Pedirlo de todos modos es un cuadro del sistema que no sirve para nada, y el
 * navegador castiga a los sitios que piden de mas. `notification-target.test.ts`
 * lo coteja contra las migraciones.
 */
export const NOTICE_PERMISSION: Record<NotificationKind, Permission> = {
  daily_report: 'report:operations',
  intake_submitted: 'intake:approve',
  intake_resolved: 'intake:submit',
  low_stock: 'purchase:manage',
  out_of_stock: 'purchase:manage',
};

/** Si a quien tiene la sesion le puede llegar algun aviso en esta sucursal. */
export function receivesNotices(can: (permission: Permission) => boolean): boolean {
  return Object.values(NOTICE_PERMISSION).some(can);
}
